import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { trackCTA } from "../lib/analytics";
import { toast } from "sonner";
import { Check, ArrowRight, Loader2, Shield, Crosshair, Award } from "lucide-react";

const TIERS = [
  {
    id: "opportunity",
    name: "Opportunity Intelligence Report",
    tagline: "For active pursuit triage",
    price: 49,
    icon: Crosshair,
    features: [
      "Full 20-section Opportunity Intelligence Report",
      "Opportunity Score, Match Score, Pursuit ROI, Win Probability",
      "FAR / DFARS clause-level risk analysis",
      "Top 10 prioritized pre-bid actions",
      "Browser PDF export + shareable link",
    ],
    cta: "Buy Opportunity Report",
  },
  {
    id: "executive",
    name: "Executive Bid/No-Bid Assessment",
    tagline: "For CEO / BD-leader briefings",
    price: 99,
    icon: Shield,
    featured: true,
    features: [
      "Everything in Opportunity Intelligence",
      "Executive-grade decisive prose tuned for C-suite review",
      "Deeper risk forensics — likelihood / impact / mitigation per risk",
      "Hidden Red Flags surfaced for senior reviewers",
      "Priority routing — first in the analysis queue",
    ],
    cta: "Buy Executive Assessment",
  },
  {
    id: "capture",
    name: "Capture Intelligence Report",
    tagline: "For strategic capture pursuits",
    price: 249,
    icon: Award,
    features: [
      "Everything in Executive Bid/No-Bid",
      "Capture strategy: discriminators, win themes, value proposition",
      "Teaming gaps + recommended partner profile",
      "Incumbent displacement playbook (when applicable)",
      "Pricing posture commentary + competitive intensity narrative",
    ],
    cta: "Buy Capture Report",
  },
];

export default function Pricing() {
  const nav = useNavigate();
  const [loadingTier, setLoadingTier] = useState(null);

  const buy = async (tierId) => {
    setLoadingTier(tierId);
    trackCTA(`pricing-buy-${tierId}`, { tier: tierId });
    try {
      const res = await api.post("/checkout/session", {
        tier: tierId,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoadingTier(null);
    }
  };

  return (
    <div className="grain min-h-screen">
      <Navbar />
      <section className="border-b border-[var(--border-soft)]">
        <div className="max-w-[1300px] mx-auto px-6 py-16">
          <div className="tick-label text-sky-400 mb-4">/ Pricing</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl text-slate-100 leading-[1.05] mb-4 max-w-3xl">
            One report. One decision.
            <br />
            <span className="text-slate-500">Priced for the deal — not the year.</span>
          </h1>
          <p className="text-slate-400 max-w-2xl text-base leading-relaxed">
            Pay per analysis. No subscriptions. No seat licenses. No procurement cycle.
            Every report is generated in minutes by Claude Sonnet 4.5 against FAR/DFARS and
            source-selection methodology.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-[1300px] mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-3 gap-px bg-[var(--border-soft)]">
            {TIERS.map((t) => (
              <div
                key={t.id}
                data-testid={`pricing-card-${t.id}`}
                className={`p-7 flex flex-col ${
                  t.featured ? "bg-[var(--bg-secondary)] ring-1 ring-sky-500/40" : "bg-[var(--bg-primary)]"
                }`}
              >
                {t.featured && (
                  <div className="inline-block self-start mb-5 bg-sky-500 text-slate-950 font-mono text-[9px] tracking-[0.25em] uppercase px-2.5 py-1">
                    Most Popular
                  </div>
                )}
                <t.icon className="w-7 h-7 text-sky-400 mb-5" strokeWidth={1.4} />
                <h3 className="font-display font-bold text-slate-100 text-xl mb-1">{t.name}</h3>
                <div className="tick-label mb-5">{t.tagline}</div>

                <div className="mb-6">
                  <span className="metric-number text-5xl text-slate-100">${t.price}</span>
                  <span className="font-mono text-sm text-slate-500 ml-2">/ report</span>
                </div>

                <ul className="space-y-2.5 mb-8 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => buy(t.id)}
                  disabled={loadingTier !== null}
                  data-testid={`pricing-cta-${t.id}`}
                  className={`inline-flex items-center justify-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-5 py-3.5 transition-colors disabled:opacity-50 ${
                    t.featured
                      ? "bg-sky-500 hover:bg-sky-400 text-slate-950"
                      : "border border-slate-700 hover:border-sky-500 hover:text-sky-400 text-slate-200"
                  }`}
                >
                  {loadingTier === t.id ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                    </>
                  ) : (
                    <>
                      {t.cta} <ArrowRight className="w-4 h-4" strokeWidth={3} />
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-500 mt-10 font-mono tracking-wider">
            Secure payments via Stripe · Cards accepted · See{" "}
            <a href="/disclaimer" className="text-sky-400 hover:underline">disclaimer</a> for refund &amp; use policy
          </p>
        </div>
      </section>
    </div>
  );
}
